import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  AcoFormData,
  TRAFFIC_FALL_BACK_TYPES,
  FALL_BACK_TYPES,
  DeepLinkWebFallBackTypes,
  NormalCreative,
  FALL_BACK_TYPE_ENUM,
} from '@tt4b/ttam-enums';
import {
  useCreativeActionTypeIs,
  useCreativeAdgroupData,
  useFallbackFormStatus,
  useStdCreativeAdgroupIs,
  useIsNotSupportProductLink,
} from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { isEmpty } from 'lodash-es';
import { computed, Ref } from '@vue/composition-api';

type CommonOptions = {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
};

type useDisabledDeepLinkAndFallBackOptions = CommonOptions & {
  createMode: Ref<string>;
  currentAdgroupIdCreativeList: Ref<NormalCreative[]>;
  isDisabledCreative: Ref<boolean>;
};

function useDisabledDeepLinkAndFallBack(
  props: CommonOptions & {
    createMode: Ref<string>;
    currentAdgroupIdCreativeList: Ref<NormalCreative[]>;
    isDisabledCreative: Ref<boolean>;
  },
) {
  const { isDisabledCreative, adgroupData, campaignData, createMode, currentAdgroupIdCreativeList, creativeData } =
    props;
  const { isShowFallbackType } = useFallbackFormStatus({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    creativeFormData: creativeData,
  });

  const { isExistAdgroup } = useCreativeActionTypeIs({
    adgroupFormData: adgroupData,
    createMode,
  });

  const { isAco } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { existAdgroupFirstCreativeInfo } = useCreativeAdgroupData({
    currentAdgroupIdCreativeList,
  });

  const disabledDeeplinkAndFallback = computed(() => {
    if (
      isShowFallbackType.value &&
      isExistAdgroup.value &&
      !isAco.value &&
      !isEmpty(existAdgroupFirstCreativeInfo.value)
    ) {
      return true;
    }

    return isDisabledCreative.value || false;
  });

  return disabledDeeplinkAndFallback;
}

export function useDeepLinkPropsDisabled(props: useDisabledDeepLinkAndFallBackOptions) {
  return {
    disabled: useDisabledDeepLinkAndFallBack(props),
  };
}

export function useFallBackPropsDisabled(props: useDisabledDeepLinkAndFallBackOptions) {
  return {
    disabled: useDisabledDeepLinkAndFallBack(props),
  };
}

export const FALL_BACK_TYPE_CUSTOM_WEB = 3;

export function useFallBackTypePropsOptions(props: CommonOptions) {
  const { adgroupData, campaignData, creativeData } = props;

  const { isStandardAdsWithCatalog } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isNotSupportProductLink } = useIsNotSupportProductLink({
    creativeData,
    adgroupData,
  });
  return {
    options: computed(() => {
      let results = TRAFFIC_FALL_BACK_TYPES();

      // TODO 这里adgroupData有点问题
      if (isStandardAdsWithCatalog.value) {
        results = FALL_BACK_TYPES();
        return [
          ...results.map(item => ({
            ...item,
            hide: isNotSupportProductLink.value && item.value === FALL_BACK_TYPE_ENUM.fallbackWeb,
          })),
          {
            // 这个类型不存在是根据external url和deeplinkWebFallBackTypes算出来的,这个值只供前端展示用
            value: FALL_BACK_TYPE_CUSTOM_WEB,
            label: i18n.t('module_adgroup_deepLink_fallback_website_custome'),
            hint: i18n.t('module_adgroup_deepLink_fallback_website_custome_hint'),
          },
        ];
      }

      return results;
    }),
  };
}

export function useFallBackTypePropsLabel(props: CommonOptions) {
  const { adgroupData, campaignData, creativeData } = props;

  const { isStandardAdsWithCatalog } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    label: computed(() => {
      if (isStandardAdsWithCatalog.value) {
        let i18nKey = '';
        switch (creativeData.value.paramsType) {
          case DeepLinkWebFallBackTypes.DefaultLink:
            i18nKey = 'module_adgroup_deepLink_fallback_app';
            break;
          case DeepLinkWebFallBackTypes.CatalogueLink:
            i18nKey = 'module_adgroup_deepLink_fallback_website_product';
            break;
          case DeepLinkWebFallBackTypes.CustomeLink:
            i18nKey = 'module_adgroup_deepLink_fallback_website_custome';
            break;
          default:
            i18nKey = '';
        }

        return i18nKey ? i18n.t(i18nKey) : '';
      } else {
        const item = TRAFFIC_FALL_BACK_TYPES().find(o => o.value === creativeData.value.fallbackType);
        return item?.label ?? '';
      }
    }),
  };
}
